import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import { HubConnectionBuilder, HttpTransportType, LogLevel, HubConnection } from "@microsoft/signalr";
import { API } from "../../api/Api";
import { DataSourceStatusChangedEvent } from "../../api/DataSourceStatus";

type SignalRConnectionRoutes = "subscribe";

export const SignalRContext = createContext<HubConnection | null>(null);
export const SignalRProvider = ({
  children,
  api,
  route,
}: // accessToken,
// getAccessToken,
{
  children: ReactNode;
  api: API;
  route: SignalRConnectionRoutes;
  // accessToken?: string;
  // getAccessToken?: () => string;
}) => {
  const fullUrl = useMemo(() => {
    return `${api.url}${route}`;
  }, [api.url, route]);

  const [hub, setHub] = useState<HubConnection | null>(null);
  useEffect(() => {
    if (!fullUrl) {
      setHub(null);
      return;
    }
    const connection = new HubConnectionBuilder()
      .withUrl(fullUrl, {
        // accessTokenFactory: getAccessToken ? () => getAccessToken() : () => accessToken || "",
        transport: HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(LogLevel.Information)
      .build();
    setHub(connection);
    connection.start().catch((err) => {
      console.warn("SignalR initial start failed:", err);
    });
    return () => {
      connection.stop().catch(() => {});
    };
  }, [fullUrl]);
  return <SignalRContext.Provider value={hub}>{children}</SignalRContext.Provider>;
};

type Callbacks =
  // AutoloadCallbacks
  | "ClientListUpdated"
  | "ClientConnectionChanged"
  | "DataSourceStatusChanged"
  | "DataSourceScheduled"
  // EntityEditSessionStateCallbacks
  | "EntityEditSessionStateChanged"
  // FileExportJobCallbacks
  | "FileExportJobProgressChanged";

export interface LiveConnectionCallbacks extends Record<Callbacks, (...args: any[]) => void> {
  // AutoloadCallbacks
  DataSourceStatusChanged: (dataSourceId: number, data: DataSourceStatusChangedEvent) => void;
  DataSourceScheduled: (dataSourceId: number, nextScheduledDate: string) => void;
  ClientListUpdated: () => void;
  ClientConnectionChanged: (bridgeId: number, isConnected: boolean) => void;
  // EntityEditSessionStateCallbacks
  EntityEditSessionStateChanged: (entityId: number, publicSessionId: string, isCurrentlyEdited: boolean) => void;
  // FileExportJobCallbacks
  FileExportJobProgressChanged: (jobId: number, userId: number, progress: number) => void;
}

export const useListen = (
  eventName: Callbacks,
  callback: LiveConnectionCallbacks[keyof LiveConnectionCallbacks],
  disabled?: boolean
) => {
  const hub = useContext(SignalRContext);
  const cbRef = useRef<LiveConnectionCallbacks[keyof LiveConnectionCallbacks]>(callback);
  useEffect(() => {
    cbRef.current = callback;
  }, [callback]);
  useEffect(() => {
    if (!hub || !eventName || !!disabled) return;
    const handler = cbRef.current;
    hub.on(eventName as string, handler);
    return () => {
      try {
        hub.off(eventName as string, handler);
      } catch {}
    };
  }, [hub, eventName, disabled]);
  return hub;
};
